import { PageController } from '@client/components/_core/layout/PageController';
import { ReplyForwardForm } from '@client/components/_shared/email/EmailDetails/ReplyForwardForm';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SkeletonBlock } from '@client/components/_ui/feedback/SkeletonBlock';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { claimFormStore } from '@client/routes/~console.$orgId/~claims/~details.$id/store';
import { trpc } from '@client/trpc/client';
import type { EmailAddress } from '@shared/types';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Alert, Modal } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

export const Route = createFileRoute('/console/$orgId/claims/details/$id/comms/reply/$messageId')({
    staticData: {
        presentation: 'overlay',
    },
    params: {
        parse({ messageId }) {
            return {
                messageId: BigInt(messageId),
            };
        },
    },
    validateSearch: zodValidator(
        z.object({
            mode: z.enum(['reply', 'forward']).default('reply'),
        }),
    ),
    component: ReplyForwardView,
});

function extractEmail(text: string): string {
    const trimmed = text.trim();
    const match = trimmed.match(/<([^>]+)>/);
    const email = match ? match[1].trim() : trimmed;
    return email.includes('@') ? email : '';
}

function ReplyForwardView() {
    const orgId = useOrgId();
    const { messageId, id: claimId } = Route.useParams();
    const { mode } = Route.useSearch();
    const navigate = useNavigate();
    const { trpcUtils } = useTrpc();
    const isReadonly = claimFormStore.use.isReadonly();

    const [open, setOpen] = useState(true);

    // CUR-725: closed claims are read-only — no replies or forwards.
    useEffect(() => {
        if (isReadonly) {
            navigate({ moveInMatches: -1 });
        }
    }, [isReadonly]);

    const {
        data: log,
        error,
        isLoading,
        isRefetching,
        refetch,
    } = trpc.communication.getCommLog.useQuery({ id: messageId });

    const navigateBack = useCallback(() => {
        navigate({
            to: '/console/$orgId/claims/details/$id/comms',
            params: { orgId, id: claimId },
            fromComponent: 'drawer',
        });
    }, [claimId, navigate, orgId]);

    const blockedByError = error?.data?.code === 'FORBIDDEN' || error?.data?.code === 'NOT_FOUND';
    const hasUnexpectedError = !!error && !blockedByError && !log;

    // Route guards: redirect if message doesn't match expected context.
    // Computed synchronously so we can early-return null before rendering any
    // reply/forward form content the user shouldn't see.
    const shouldBlock =
        !isLoading &&
        (blockedByError ||
            (!!log &&
                (log.channel !== 'EMAIL' ||
                    log.claimId !== BigInt(claimId) ||
                    (mode === 'reply' && log.direction !== 'INBOUND'))));

    useEffect(() => {
        // `!isReadonly` prevents a duplicate transition when both guards fire — the
        // closed-claim effect above already navigated away.
        if (shouldBlock && !isReadonly) {
            navigateBack();
        }
    }, [navigateBack, shouldBlock, isReadonly]);

    const handleClose = (refresh?: boolean) => {
        setOpen(false);

        if (refresh) {
            void trpcUtils.communication.listCommLogAndNotes.invalidate({ claimId: BigInt(claimId) });
            void trpcUtils.claim.getClaimClientUpdateSummary.invalidate({ id: BigInt(claimId) });
        }
    };

    const mapToEmailAddresses = (
        recipients: Array<{ email?: string; name?: string | null }> | null | undefined,
    ): EmailAddress[] | null => {
        if (!recipients) return null;
        return recipients
            .filter((r): r is typeof r & { email: string } => Boolean(r.email))
            .map((r) => ({ email: r.email, name: r.name ?? undefined }));
    };

    if (shouldBlock) {
        return null;
    }

    return (
        <PageController route={Route}>
            <Modal
                open={open}
                width={900}
                footer={false}
                title={mode === 'reply' ? 'Reply' : 'Forward'}
                onCancel={() => handleClose()}
                afterClose={navigateBack}
                destroyOnHidden
                centered
                styles={{
                    body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', scrollbarGutter: 'stable' },
                }}
                classNames={{ body: 'pt-2 pr-3 [scrollbar-width:thin]' }}
            >
                {hasUnexpectedError ? (
                    <div className="space-y-4 py-2">
                        <Alert
                            type="error"
                            showIcon
                            title="Unable to load this email"
                            description="Try again, or close this dialog and reopen it."
                        />
                        <div className="flex justify-end">
                            <Button type="secondary" onClick={() => void refetch()} loading={isRefetching}>
                                Try Again
                            </Button>
                        </div>
                    </div>
                ) : isLoading || (!log && !blockedByError) ? (
                    <SkeletonBlock variant="form" rows={4} />
                ) : (
                    log && (
                        <ReplyForwardForm
                            mode={mode}
                            email={{
                                to: mapToEmailAddresses(log.to),
                                cc: mapToEmailAddresses(log.cc),
                                bcc: mapToEmailAddresses(log.bcc),
                                subject: log.subject || '',
                                content: log.content || '',
                                messageId: log.messageId,
                                sender: log.sender?.email
                                    ? log.sender
                                    : { isInternal: false, email: extractEmail(log.from || '') },
                                createdAt: log.createdAt,
                                direction: log.direction,
                            }}
                            claimId={BigInt(claimId)}
                            onClose={handleClose}
                        />
                    )
                )}
            </Modal>
        </PageController>
    );
}
